import React, { useState } from 'react';
import { Colors } from 'library';
import { Modal, ModalFooter } from './Modal';
import { PillButton } from './PillButton';
import { Stack } from './Stack';
import { Text } from './Text';
import { Toast } from './Toast';

interface DeleteWithConfirmationProps {
    button: React.ReactElement;
    message: string;
    onSubmit: () => Promise<void>;
    title?: string;
    confirmLabel?: string;
    successMessage?: string;
    errorMessage?: string;
    onError?: (error: unknown) => void;
}

export function DeleteWithConfirmation({
    button,
    message,
    onSubmit,
    title = 'Confirm Delete',
    confirmLabel = 'Delete',
    successMessage = 'Deleted',
    errorMessage = 'Could not delete',
    onError,
}: DeleteWithConfirmationProps) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<string | null>(null);

    function showToast(msg: string) {
        setToast(msg);
        setTimeout(() => setToast(null), 3000);
    }

    async function handleConfirm() {
        setSubmitting(true);
        try {
            await onSubmit();
            setOpen(false);
            showToast(successMessage);
        } catch (error) {
            if (onError) {
                setOpen(false);
                onError(error);
            } else {
                showToast(errorMessage);
            }
        } finally {
            setSubmitting(false);
        }
    }

    const trigger = React.cloneElement(button, {
        onClick: (e: React.MouseEvent) => {
            button.props.onClick?.(e);
            setOpen(true);
        },
    });

    return (
        <>
            {trigger}
            {open && (
                <Modal title={title} onClose={() => !submitting && setOpen(false)} width={440}>
                    <Stack gap="md">
                        <Text>{message}</Text>
                        <ModalFooter>
                            <PillButton variant="secondary" onClick={() => setOpen(false)} disabled={submitting}>
                                Cancel
                            </PillButton>
                            <PillButton onClick={handleConfirm} disabled={submitting} color={Colors.error}>
                                {submitting ? 'Deleting...' : confirmLabel}
                            </PillButton>
                        </ModalFooter>
                    </Stack>
                </Modal>
            )}
            <Toast message={toast} />
        </>
    );
}
